import React, { useRef, useEffect, useMemo, memo, useCallback } from 'react'
import styled from 'styled-components'
import type { ChatRequestOptions } from 'ai'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import SyntaxHighlighter, { SyntaxHighlighterProps } from 'react-syntax-highlighter'
import { atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import MessageBubble from './messageBubble'
import ScrollToBottomButton from './scrollToBottomButton'
import useLLMChat, { MessagePart, TextUIPart, type LLMChatMessage } from './hooks/useLLMChat'
import type { ArtificialIntelligenceResponseAnalytics } from './hooks/useLLMChat'
import { SuggestedQuestions } from './suggestedQuestions'
import {
  ToolCallContainer,
  ToolCallBox,
  ToolCallTag,
  ToolInvocation,
  ToolInvocationUIPart,
} from '~/packages/llmChatMessage/create/llmChatMessageCreateTool.component'
// import {
//   NewBlankNotebookRenderer,
//   AddMarkdownRenderer,
//   AddCodeRenderer,
// } from '~/packages/llmChatMessage/create/llmChatMessageCreateNotebook.component'
// import { SequentialThinkingRenderer } from '~/packages/llmChatMessage/create/llmChatMessageCreateSequentialThinking.component'
import MessageInput from './messageInput'
import removeMarkdown from 'remove-markdown'
import ExpandableImage from './expandableImage'
import MessageSkeleton from './messageSkeleton'
import { isDevelopment } from '~/utils/shared/platform'
import { isUuid } from '~/utils/shared/isUuid'
import ShowInsightCards from './showInsightCards'
import { trackAnalyticsEvent } from '~/utils/client/analytics'

const MessagesArea = styled.div`
  flex: 1;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;

  @media screen and (max-width: 1150px) {
    padding: 0px 10px;
  }
`

const CostInfo = styled.div`
  font-size: 10px;
  opacity: 0.6;
  margin-top: 4px;
  font-family: 'Figtree', Arial, sans-serif;
  color: #666;
`

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;

  &::after {
    content: '▋';
    animation: blink 1s infinite;
    opacity: 0.7;
  }

  @keyframes blink {
    0%,
    50% {
      opacity: 0.7;
    }
    51%,
    100% {
      opacity: 0;
    }
  }
`

export const StreamingCursor = styled.span`
  &::after {
    content: '▋';
    animation: blink 1s infinite;
    opacity: 0.7;
    margin-left: 2px;
  }

  @keyframes blink {
    0%,
    50% {
      opacity: 0.7;
    }
    51%,
    100% {
      opacity: 0;
    }
  }
`

const InputSection = styled.div`
  position: sticky;
  bottom: 0px;
  width: 100%;
  padding-bottom: 20px;
  z-index: 1;
  background: rgba(235, 235, 235, 1);
  box-shadow: 0 0px 30px 30px rgba(235, 235, 235, 1);

  @media screen and (max-width: 1150px) {
    padding: 0px 10px 10px;
  }
`

const InputSectionContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`

const toolRenderers: Record<string, React.FC<{ invocation: ToolInvocation }>> = {
  // new_blank_notebook: NewBlankNotebookRenderer,
  // sequentialthinking: SequentialThinkingRenderer,
  // add_markdown: AddMarkdownRenderer,
  // add_code: AddCodeRenderer,
  // replace_code: AddCodeRenderer,
}

const TOOL_LABELS: Record<string, string> = {
  list_tables: 'Verificando dados existentes',
  describe_table: 'Conhecendo estruturas de dados',
  run_postgres_query: 'Consultando dados',
  sequential_thinking: 'Fazendo reflexão avançada',
  top_insights_by_relevance: 'Identificando os principais insights',
}

const TOOL_LABELS_DONE: Record<string, string> = {
  list_tables: 'Verificou dados existentes',
  describe_table: 'Conheceu estruturas de dados',
  run_postgres_query: 'Consultou dados',
  sequential_thinking: 'Reflexão concluída',
  top_insights_by_relevance: 'Identificou os principais insights',
}

function DefaultToolRenderer({ invocation }: { invocation: ToolInvocation }): JSX.Element {
  const isDone =
    invocation.state === 'result' ||
    (invocation.state as string) === 'output-available' ||
    (invocation.state as string) === 'output-error'

  const labelMap = isDone ? TOOL_LABELS_DONE : TOOL_LABELS
  const label = labelMap[invocation.toolName] ?? invocation.toolName

  return <ToolCallTag $variant={isDone ? 'done' : 'active'}>{label}</ToolCallTag>
}

function renderToolCall(part: ToolInvocationUIPart, index: number): JSX.Element {
  console.log('part.toolInvocation', part.toolInvocation)
  const Renderer = toolRenderers[part.toolInvocation.toolName] ?? DefaultToolRenderer
  return <Renderer key={index} invocation={part.toolInvocation} />
}

/**
 * Extrai todo o conteúdo de texto de uma mensagem, incluindo partes do assistente e toolCalls
 */
function extractMessageContent(
  message: LLMChatMessage & { parts?: MessagePart[]; toolCalls?: any[] },
): string {
  let content = ''

  // Adiciona texto das partes do assistente
  if (message.parts) {
    const textParts = message.parts
      .filter((part): part is TextUIPart => part.type === 'text')
      .map(part => part.text)
    content += '\n' + textParts.join('\n')
  }

  // Adiciona texto dos toolCalls
  if (message.toolCalls) {
    const toolCallsText = message.toolCalls
      .map(toolCall => {
        if (toolCall.toolName === 'add_markdown' && toolCall.callArguments?.markdown) {
          return toolCall.callArguments.markdown
        }
        // if (typeof toolCall.callResponse === 'string') {
        //   return toolCall.callResponse
        // }
        return ''
      })
      .filter(Boolean)
    content += '\n' + toolCallsText.join('\n')
  }

  return removeMarkdown(content.trim())
}

type UIToolCallForUI = {
  id: string
  toolName: string
  callArguments?: unknown
  callResponse?: unknown
  state?: string
}

/**
 * Extrai chamadas de ferramenta a partir das parts de UIMessage (tool-* e dynamic-tool).
 * Isso permite enxergar o uso de tools em tempo real, mesmo antes de salvar no banco.
 */
function getToolCallsFromParts(parts?: MessagePart[]): UIToolCallForUI[] {
  if (!parts) return []

  const toolCallsById = new Map<string, UIToolCallForUI>()

  const upsertToolCall = (id: string, patch: Partial<UIToolCallForUI>) => {
    const existing = toolCallsById.get(id)
    if (!existing && !patch.toolName) return

    const next: UIToolCallForUI = {
      ...(existing ?? { id, toolName: patch.toolName as string }),
      ...patch,
    }

    // Evitar sobrescrever um toolName válido com string vazia.
    if (existing?.toolName && !patch.toolName) next.toolName = existing.toolName

    toolCallsById.set(id, next)
  }

  for (const rawPart of parts as any[]) {
    if (!rawPart || typeof rawPart !== 'object') continue
    const type = rawPart.type
    if (!type || typeof type !== 'string') continue

    // Formato mais recente do AI SDK: { type: 'tool-invocation', toolInvocation: { ... } }
    if (type === 'tool-invocation' && rawPart.toolInvocation) {
      const toolInvocation = rawPart.toolInvocation as any
      const toolCallId = toolInvocation.toolCallId ?? toolInvocation.id
      const toolName = toolInvocation.toolName
      if (!toolCallId || !toolName) continue

      const state = toolInvocation.state
      const shouldIncludeArguments = state !== 'partial-call'

      upsertToolCall(String(toolCallId), {
        toolName: String(toolName),
        ...(shouldIncludeArguments ? { callArguments: toolInvocation.args } : {}),
        ...(toolInvocation.result !== undefined ? { callResponse: toolInvocation.result } : {}),
        ...(state !== undefined ? { state } : {}),
      })
      continue
    }

    if (type === 'dynamic-tool') {
      const toolCallId = rawPart.toolCallId
      const toolName = rawPart.toolName
      if (!toolCallId || !toolName) continue

      const state = rawPart.state
      const shouldIncludeArguments = state !== 'partial-call'

      upsertToolCall(String(toolCallId), {
        toolName: String(toolName),
        ...(shouldIncludeArguments ? { callArguments: rawPart.input } : {}),
        ...(rawPart.output !== undefined ? { callResponse: rawPart.output } : {}),
        ...(state !== undefined ? { state } : {}),
      })
    } else if (type.startsWith('tool-')) {
      const toolCallId = rawPart.toolCallId
      if (!toolCallId) continue

      const isToolStreamEvent = /^tool-(input|output)-/.test(type)

      if (isToolStreamEvent) {
        const toolName = typeof rawPart.toolName === 'string' ? rawPart.toolName : undefined
        const state = type.replace(/^tool-/, '')

        if (type === 'tool-input-start') {
          if (!toolName) continue
          upsertToolCall(String(toolCallId), { toolName, state })
          continue
        }

        // Para deltas de input (stream), NÃO propagar args parciais para a UI.
        if (type === 'tool-input-available') {
          if (!toolName) continue
          upsertToolCall(String(toolCallId), {
            toolName,
            callArguments: rawPart.input,
            state,
          })
          continue
        }

        if (type === 'tool-output-available') {
          if (!toolName) continue
          upsertToolCall(String(toolCallId), {
            toolName,
            callResponse: rawPart.output,
            state,
          })
          continue
        }

        if (type === 'tool-output-error') {
          if (!toolName) continue
          upsertToolCall(String(toolCallId), {
            toolName,
            callArguments: rawPart.input ?? rawPart.rawInput,
            callResponse: rawPart.output,
            state,
          })
          continue
        }

        // tool-input-delta / etc.: ignorar por enquanto.
        continue
      }

      // Formato antigo: type = 'tool-<toolName>' com input/output prontos.
      const toolName = type.replace(/^tool-/, '')
      upsertToolCall(String(toolCallId), {
        toolName,
        callArguments:
          rawPart.state === 'output-error' ? (rawPart.input ?? rawPart.rawInput) : rawPart.input,
        callResponse: rawPart.output,
        state: rawPart.state,
      })
    }
  }

  return Array.from(toolCallsById.values())
}

/**
 * Mescla toolCalls vindas do histórico (banco) com as derivadas das parts,
 * priorizando as informações mais completas por id.
 */
function mergeToolCalls(
  persistedToolCalls?: any[],
  partToolCalls?: UIToolCallForUI[],
): UIToolCallForUI[] {
  const merged = new Map<string, UIToolCallForUI>()

  for (const toolCall of persistedToolCalls ?? []) {
    if (!toolCall) continue
    const id = String(toolCall.id ?? toolCall.toolCallId ?? '')
    if (!id) continue

    merged.set(id, {
      id,
      toolName: toolCall.toolName,
      callArguments: toolCall.callArguments,
      callResponse: toolCall.callResponse,
      state: toolCall.state,
    })
  }

  for (const toolCall of partToolCalls ?? []) {
    if (!toolCall || !toolCall.id) continue

    const existing = merged.get(toolCall.id)
    merged.set(toolCall.id, {
      ...(existing ?? {}),
      ...toolCall,
    })
  }

  return Array.from(merged.values())
}

function safeJsonParse(value: string) {
  try {
    return JSON.parse(value) as unknown
  } catch {
    return null
  }
}

function getInsightIdsFromTopInsightsByRelevanceToolCalls(toolCalls: UIToolCallForUI[]): string[] {
  const insightIds: string[] = []
  const seenInsightIds = new Set<string>()

  for (const toolCall of toolCalls) {
    if (!toolCall || toolCall.toolName !== 'top_insights_by_relevance') continue

    const rawResponse = toolCall.callResponse
    const parsedResponse =
      typeof rawResponse === 'string' ? safeJsonParse(rawResponse) : rawResponse
    const insights = (parsedResponse as any)?.insights

    if (!Array.isArray(insights)) continue

    for (const insight of insights) {
      const insightId = (insight as any)?.id
      if (!insightId || typeof insightId !== 'string') continue
      if (!isUuid(insightId)) continue
      if (seenInsightIds.has(insightId)) continue
      seenInsightIds.add(insightId)
      insightIds.push(insightId)
    }
  }

  return insightIds
}

function getShowInsightIdsFromToolCalls(toolCalls: UIToolCallForUI[]): string[] {
  const insightIds: string[] = []
  const seenInsightIds = new Set<string>()

  for (const toolCall of toolCalls) {
    if (!toolCall || toolCall.toolName !== 'show_insight') continue

    const rawArguments = toolCall.callArguments
    const parsedArguments =
      typeof rawArguments === 'string' ? safeJsonParse(rawArguments) : rawArguments

    const insightId = (parsedArguments as any)?.insightId
    if (!insightId || typeof insightId !== 'string') continue
    if (!isUuid(insightId)) continue
    if (seenInsightIds.has(insightId)) continue

    seenInsightIds.add(insightId)
    insightIds.push(insightId)
  }

  return insightIds
}

/**
 * Renderiza as partes do assistente (texto + tool-calls) preservando a ordem.
 */
function renderAssistantParts(parts: MessagePart[], onLoadImage?: () => void): JSX.Element[] {
  return parts
    .map((part, index) => {
      const rawPart: any = part

      if (part.type === 'text' && typeof (part as TextUIPart).text === 'string') {
        return (
          <ReactMarkdown
            key={index}
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              p({ node, ...props }) {
                return <p style={{ margin: '0 0 4px' }} {...props} />
              },
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '')

                return match ? (
                  <SyntaxHighlighter
                    style={atomOneLight as SyntaxHighlighterProps['style']}
                    language={match[1]}
                    PreTag='div'
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                )
              },
              img({ src, alt }) {
                return <ExpandableImage src={src || ''} alt={alt} onLoad={onLoadImage} />
              },
            }}
          >
            {(part as TextUIPart).text}
          </ReactMarkdown>
        )
      }

      // Tool calls vindas diretamente do stream de UI messages (dynamic-tool / tool-*)
      if (rawPart && typeof rawPart.type === 'string') {
        if (rawPart.type === 'dynamic-tool') {
          if (
            rawPart.toolName === 'report_missing_feature_or_data' ||
            rawPart.toolName === 'show_insight'
          ) {
            return null
          }

          const invocation: ToolInvocation = {
            toolCallId: String(rawPart.toolCallId ?? ''),
            toolName: String(rawPart.toolName ?? ''),
            state: rawPart.state as any,
            args: rawPart.input,
            result: rawPart.output,
          }
          const Renderer = toolRenderers[invocation.toolName] ?? DefaultToolRenderer
          return <Renderer key={index} invocation={invocation} />
        }

        if (rawPart.type.startsWith('tool-')) {
          // Eventos do stream de tool input/output não devem aparecer como "tools" na UI.
          if (/^tool-(input|output)-/.test(rawPart.type)) return null

          const toolName = rawPart.type.replace(/^tool-/, '')
          if (toolName === 'report_missing_feature_or_data' || toolName === 'show_insight') {
            return null
          }
          const invocation: ToolInvocation = {
            toolCallId: String(rawPart.toolCallId ?? ''),
            toolName,
            state: rawPart.state as any,
            args:
              rawPart.state === 'output-error'
                ? (rawPart.input ?? rawPart.rawInput)
                : rawPart.input,
            result: rawPart.output,
          }
          const Renderer = toolRenderers[toolName] ?? DefaultToolRenderer
          return <Renderer key={index} invocation={invocation} />
        }
      }

      return null
    })
    .filter(Boolean) as JSX.Element[]
}

interface MessageListProps {
  chatId: number
  token: string
  historyMessages?: any[]
  firstMessage?: string
  isArchived?: boolean
}

const MessageAreaComponent = ({
  messages,
  isLoading,
  formatCostFromUsage,
  formatSavedCost,
  lastUsage,
  reload,
  isArchived,
  bottomElementReference,
  onSelectSuggestedQuestion,
}: {
  messages: LLMChatMessage[]
  isLoading: boolean
  isArchived: boolean
  bottomElementReference: React.RefObject<HTMLDivElement>
  lastUsage: any
  formatCostFromUsage?: (usage: any) => string | null
  formatSavedCost?: (costUsd: number | null) => string | null
  reload?: (chatRequestOptions?: ChatRequestOptions) => Promise<void>
  onSelectSuggestedQuestion?: (question: string) => void
}) => {
  return messages.length > 0 ? (
    messages.map((message, index) => {
      const isLastMessage = index === messages.length - 1
      const isStreamingAssistantMessage = isLoading && isLastMessage && message.role === 'assistant'
      const isLastAssistantMessage = isLastMessage && message.role === 'assistant' && !isLoading

      const messageParts: MessagePart[] | undefined = (message as any).parts as
        | MessagePart[]
        | undefined

      const hasInlineToolParts =
        messageParts?.some(part => {
          const rawPart: any = part
          const type = rawPart?.type
          return (
            type === 'dynamic-tool' ||
            (typeof type === 'string' &&
              type.startsWith('tool-') &&
              !/^tool-(input|output)-/.test(type))
          )
        }) ?? false

      const textParts = messageParts?.filter(part => part.type === 'text') as
        | TextUIPart[]
        | undefined
      const textContent = textParts?.map(part => part.text).join('') ?? ''

      const persistedToolCalls = (message as any).toolCalls as Array<any> | undefined
      const toolCallsFromParts = getToolCallsFromParts(messageParts)
      const toolCalls = mergeToolCalls(persistedToolCalls, toolCallsFromParts)
      const showInsightIds = Array.from(
        new Set([
          ...getShowInsightIdsFromToolCalls(toolCalls),
          ...getInsightIdsFromTopInsightsByRelevanceToolCalls(toolCalls),
        ]),
      )

      const savedCostUsd =
        (message as any).costUsd ?? message.metadata?.costUsd ?? message.metadata?.estimatedCostUsd

      const hasMissingFeatureTool =
        toolCalls &&
        toolCalls.some(toolCall => toolCall.toolName === 'report_missing_feature_or_data')

      let costToShow = null
      if (isDevelopment) {
        if (isLastAssistantMessage && lastUsage && formatCostFromUsage) {
          costToShow = formatCostFromUsage(lastUsage)
        } else if (savedCostUsd && message.role === 'assistant' && formatSavedCost) {
          costToShow = formatSavedCost(savedCostUsd)
        }
      }

      return (
        <MessageBubble
          key={message.id}
          type={message.role === 'user' ? 'user' : 'ai'}
          onRegenerate={!isArchived && isLastAssistantMessage && reload ? reload : undefined}
          copyContent={extractMessageContent(
            message as LLMChatMessage & { parts?: MessagePart[]; toolCalls?: any[] },
          )}
          isThinking={isStreamingAssistantMessage}
        >
          {message.role === 'assistant' ? (
            messageParts && messageParts.length > 0 ? (
              <>
                {renderAssistantParts(messageParts, () => {
                  bottomElementReference.current?.scrollIntoView({ behavior: 'smooth' })
                })}
                {isStreamingAssistantMessage && <StreamingCursor />}
              </>
            ) : (
              <span>
                {textContent}
                {isStreamingAssistantMessage && <StreamingCursor />}
              </span>
            )
          ) : (
            textContent
          )}

          {toolCalls &&
            toolCalls.length > 0 &&
            !hasInlineToolParts &&
            // report_missing_feature_or_data já tem visualização dedicada (Perguntas que você pode fazer)
            toolCalls.some(
              toolCall =>
                toolCall.toolName !== 'report_missing_feature_or_data' &&
                toolCall.toolName !== 'show_insight',
            ) && (
              <div style={{ marginTop: 8 }}>
                {toolCalls.map(toolCall => {
                  if (
                    toolCall.toolName === 'report_missing_feature_or_data' ||
                    toolCall.toolName === 'show_insight'
                  ) {
                    return null
                  }

                  const Renderer = toolRenderers[toolCall.toolName] ?? DefaultToolRenderer
                  return (
                    <Renderer
                      key={toolCall.id}
                      invocation={{
                        toolCallId: toolCall.id,
                        toolName: toolCall.toolName,
                        state: (toolCall.state as any) ?? 'result',
                        args: toolCall.callArguments,
                        result: toolCall.callResponse,
                      }}
                    />
                  )
                })}
              </div>
            )}

          {hasMissingFeatureTool && onSelectSuggestedQuestion && (
            <div style={{ marginTop: 12 }}>
              <SuggestedQuestions onSelect={onSelectSuggestedQuestion} disabled={isLoading} />
            </div>
          )}

          {message.role === 'assistant' &&
            showInsightIds.length > 0 &&
            !isStreamingAssistantMessage && <ShowInsightCards insightIds={showInsightIds} />}

          {costToShow && <CostInfo>{costToShow}</CostInfo>}
        </MessageBubble>
      )
    })
  ) : (
    <MessageSkeleton />
  )
}

export const MessageAreaList = memo(MessageAreaComponent, (prevProps, nextProps) => {
  return (
    prevProps.messages &&
    prevProps.messages.length === nextProps.messages.length &&
    prevProps.messages.every((msg, index) => msg.id === nextProps.messages[index].id) &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isArchived === nextProps.isArchived &&
    prevProps.bottomElementReference === nextProps.bottomElementReference &&
    prevProps.lastUsage === nextProps.lastUsage &&
    prevProps.formatCostFromUsage === nextProps.formatCostFromUsage &&
    prevProps.formatSavedCost === nextProps.formatSavedCost &&
    prevProps.reload === nextProps.reload &&
    prevProps.onSelectSuggestedQuestion === nextProps.onSelectSuggestedQuestion
  )
})

export default function MessageList({
  chatId,
  token,
  firstMessage,
  historyMessages,
  isArchived,
}: MessageListProps) {
  const bottomElementReference = useRef<HTMLDivElement>(null)
  const inputReference = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    inputReference.current?.focus()
  }, [chatId])

  const handleResponse = (response: ArtificialIntelligenceResponseAnalytics) => {
    const usage = response.usage ?? null
    if (response.isError) {
      return
    }

    trackAnalyticsEvent('ai_response_received', {
      ai_interaction_category: 'chat_message',
      ai_cached_input_tokens: usage?.cachedInputTokens,
      ai_estimated_cost_usd: usage?.estimatedCostUsd ?? usage?.costUsd ?? undefined,
      ai_finish_reason: response.finishReason ?? usage?.finishReason ?? undefined,
      ai_input_tokens: usage?.inputTokens,
      ai_model_key: usage?.modelKey ?? undefined,
      ai_output_tokens: usage?.outputTokens,
      ai_tool_calls_count: usage?.toolCallsCount ?? undefined,
      ai_total_tokens: usage?.totalTokens,
      source_screen: 'ai_chat',
    })
    setTimeout(() => {
      inputReference.current?.focus()
    }, 50)
  }

  const chat = useLLMChat({
    chatId: chatId,
    token: token,
    firstMessage: firstMessage,
    historyMessages,
    onResponse: handleResponse,
  })

  useEffect(() => {
    bottomElementReference.current?.scrollIntoView({ behavior: chat.isLoading ? 'auto' : 'smooth' })
  }, [chat.messages, chat.isLoading])

  const handleSelectSuggestedQuestion = (question: string) => {
    chat.sendQuickQuestion(question)
  }

  const handleMessageSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      trackAnalyticsEvent('ai_prompt_submitted', {
        ai_interaction_category: 'chat_message',
        feature: 'ai_chat',
        source_screen: 'ai_chat',
      })
      chat.handleSubmit(e)
    },
    [chat.handleSubmit],
  )

  return (
    <>
      <MessagesArea>
        <MessageAreaList
          messages={chat.messages}
          isLoading={chat.isLoading}
          isArchived={isArchived || false}
          bottomElementReference={bottomElementReference}
          lastUsage={chat.lastUsage}
          formatCostFromUsage={chat.formatCostFromUsage}
          formatSavedCost={chat.formatSavedCost}
          reload={chat.reload}
          onSelectSuggestedQuestion={handleSelectSuggestedQuestion}
        />

        {chat.isLoading &&
          chat.messages.length > 0 &&
          chat.messages[chat.messages.length - 1]?.role === 'user' && (
            <MessageBubble type='ai' isThinking>
              <TypingIndicator>Prix AI está pensando…</TypingIndicator>
            </MessageBubble>
          )}

        {chat.error && (
          <MessageBubble
            type='ai'
            onRegenerate={!isArchived && chat.reload ? chat.reload : undefined}
          >
            Erro: {chat.error.message}
          </MessageBubble>
        )}

        <div ref={bottomElementReference} />
      </MessagesArea>

      <InputSection>
        <InputSectionContent>
          {bottomElementReference.current && (
            <ScrollToBottomButton targetRef={bottomElementReference} />
          )}
          {!isArchived && (
            <MessageInput
              inputRef={inputReference}
              input={chat.input}
              setInput={chat.setInput}
              onSubmit={handleMessageSubmit}
              onStop={chat.stop}
              isLoading={chat.isLoading || chat.messages.length === 0}
              isThinking={chat.status === 'streaming'}
            />
          )}
        </InputSectionContent>
      </InputSection>
    </>
  )
}
